import React, { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDown, Mail, Trash } from "lucide-react";
import { UserRole } from "../../../../services/UserService";
import { InvitedUser } from "../../../../models/UserModels";
import { formatRole } from "../../../../utils/RoleUtils";
import { getAvailableInviteRoles } from "../../../../utils/rolesData";

const InviteMembersModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onProceed: (invitedUsers: InvitedUser[]) => void;
  currentUserRole: UserRole;
}> = ({ open, onClose, onProceed, currentUserRole }) => {
  // Get available roles based on invitation rules
  const availableRoles = useMemo(() => {
    // UserRole enum values are already lowercase strings matching the expected type
    const roleData = getAvailableInviteRoles(
      currentUserRole as "superadmin" | "owner" | "admin" | "member"
    );
    // Convert Role objects to UserRole enum values
    return roleData.map((r) => {
      // Map role id to UserRole enum
      const roleMap: Record<string, UserRole> = {
        superadmin: UserRole.SuperAdmin,
        owner: UserRole.Owner,
        admin: UserRole.Admin,
        member: UserRole.Member,
      };
      return roleMap[r.id] || UserRole.Member;
    });
  }, [currentUserRole]);

  const [role, setRole] = useState<UserRole>(
    availableRoles[0] || UserRole.Member
  );
  const [email, setEmail] = useState<string>("");
  const [users, setUsers] = useState<InvitedUser[]>([]);
  const [error, setError] = useState<string>("");
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef<HTMLDivElement>(null);

  // Sanitize email input - remove emojis and invalid characters
  const sanitizeEmailInput = (value: string): string => {
    // Remove emojis and non-ASCII characters except valid email characters
    // Allow: letters, numbers, dots, underscores, hyphens, plus signs, @ symbol
    return value.replace(/[^\w.@+-]/g, "");
  };

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    if (!email || !email.trim()) return false;
    
    // Basic email regex - requires @ and valid domain
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const trimmed = email.trim();
    
    // Check basic format
    if (!emailRegex.test(trimmed)) return false;
    
    // Additional checks
    if (trimmed.startsWith(".") || trimmed.startsWith("@")) return false;
    if (trimmed.endsWith(".") || trimmed.endsWith("@")) return false;
    if (trimmed.includes("..")) return false; // No consecutive dots
    
    return true;
  };

  const handleAdd = () => {
    const trimmedEmail = email.trim();
    
    // Clear previous errors
    setError("");
    
    // Validate email
    if (!trimmedEmail) {
      setError("Please enter an email address.");
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError("Please enter a valid email address (e.g., user@example.com).");
      return;
    }

    // Check for duplicates (case-insensitive)
    const normalizedEmail = trimmedEmail.toLowerCase();
    if (users.some((user) => user.email.toLowerCase() === normalizedEmail)) {
      setError("This email has already been added to the list.");
      return;
    }

    if (users.length >= 4) {
      setError("You can only invite up to 4 members at once.");
      return;
    }

    // Validate role is in available roles
    if (!availableRoles.includes(role)) {
      setError("Selected role is not available for your current permissions.");
      return;
    }

    // Add user with normalized email and role
    setUsers([...users, { email: normalizedEmail, role: role as UserRole }]);
    setEmail("");
    setError("");
  };

  const handleRemove = (index: number) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  const handleProceed = () => {
    if (users.length === 0) {
      setError("Please add at least one user to invite.");
      return;
    }

    // Validate that all selected roles are in available roles
    const invalidRoles = users.filter(
      (user) => !availableRoles.includes(user.role)
    );
    if (invalidRoles.length > 0) {
      setError(
        "Some selected roles are not allowed. Please remove and re-add those users."
      );
      return;
    }

    onProceed(users);
    setUsers([]);
    setRole(availableRoles[0] || UserRole.Member);
    onClose();
  };

  // Update selected role when available roles change
  useEffect(() => {
    if (availableRoles.length > 0 && !availableRoles.includes(role)) {
      setRole(availableRoles[0]);
    }
  }, [availableRoles, role]);

  // Handle click outside for role dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target as Node)
      ) {
        setIsRoleDropdownOpen(false);
      }
    };

    if (isRoleDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isRoleDropdownOpen]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="w-full max-w-lg bg-background-paper dark:bg-background-paperDark rounded-lg shadow-xl p-6 relative">
        <div className="border-b border-divider dark:border-divider-dark pb-4 mb-6 relative">
          <h2 className="text-2xl font-bold text-text-primary dark:text-text-primaryDark">
            Invite Members
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
            Invite people to collaborate in your organization. Assign roles and
            send out invitations.
          </p>
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-0 right-0 text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-text-primary dark:text-text-primaryDark"
            >
              Email Address
            </label>
            <div className="relative mt-1">
              <input
                id="email"
                type="email"
                placeholder="Enter email address"
                value={email}
                maxLength={254}
                onChange={(e) => {
                  const sanitized = sanitizeEmailInput(e.target.value);
                  setEmail(sanitized);
                  // Clear error when user starts typing
                  if (error) {
                    setError("");
                  }
                }}
                onBlur={() => {
                  // Validate on blur if there's content
                  if (email.trim() && !isValidEmail(email)) {
                    setError("Please enter a valid email address.");
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                className={`w-full p-3 pl-10 border rounded-md bg-background-paper dark:bg-background-panelDark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:ring-2 focus:ring-secondary outline-none transition-colors ${
                  error
                    ? "border-red-500 dark:border-red-400"
                    : "border-divider dark:border-divider-dark"
                }`}
              />
              <Mail className="absolute left-3 top-3.5 text-text-secondary dark:text-text-secondaryDark w-5 h-5" />
            </div>
            {error && (
              <p className="text-sm text-red-500 dark:text-red-400 mt-2">
                {error}
              </p>
            )}
          </div>

          <div>
            <label
              className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-1"
              htmlFor="role"
            >
              Assign Role
            </label>
            <div className="relative" ref={roleDropdownRef}>
              <button
                type="button"
                onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                className="w-full px-3.5 py-2 text-sm font-medium bg-background-paper dark:bg-background-paperDark border border-divider/60 dark:border-divider-dark/60 rounded-lg text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-divider dark:hover:border-divider-dark transition-all duration-150 flex items-center gap-2 justify-between shadow-sm hover:shadow"
              >
                <span>{formatRole(role)}</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    isRoleDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isRoleDropdownOpen && (
                <div className="absolute top-full left-0 mt-1.5 bg-background-paper dark:bg-background-paperDark border border-divider/60 dark:border-divider-dark/60 rounded-lg shadow-xl z-50 w-full overflow-hidden backdrop-blur-sm">
                  {availableRoles.length > 0 ? (
                    availableRoles.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => {
                        setRole(r);
                        setIsRoleDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors ${
                        role === r
                          ? "bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary font-medium"
                          : "text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                      }`}
                    >
                      {formatRole(r)}
                    </button>
                    ))
                  ) : (
                    <div className="px-3.5 py-2.5 text-sm text-text-secondary dark:text-text-secondaryDark">
                      No roles available to invite
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleAdd}
            className="w-full py-2 border-2 border-secondary text-secondary rounded-md transition font-medium 
             hover:bg-secondary hover:text-white"
          >
            Add to Invitation List
          </button>
        </div>

        {users.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primaryDark">
              Invitation List
            </h3>
            <ul className="space-y-3 mt-4">
              {users.map((user, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between p-3 bg-background-panel dark:bg-background-panelDark rounded-md"
                >
                  <div>
                    <p className="text-sm text-text-primary dark:text-text-primaryDark">
                      {user.email}
                    </p>
                    <p className="text-xs text-text-secondary dark:text-text-secondaryDark">
                      {formatRole(user.role)}{" "}
                      {/* ✅ Displays formatted role in UI */}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove(index)}
                    className="text-red-500 hover:text-red-600 transition"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </li>
              ))}
            </ul>
            <button
              onClick={handleProceed}
              className="w-full mt-4 py-2 bg-black text-white hover:bg-black/90 dark:bg-secondary dark:hover:bg-secondary-dark transition font-medium"
            >
              Send Invitations
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteMembersModal;
